import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { OUTREACH_RAMP } from 'fym-shared/src/music24/outreach/mailboxRamp';
import { TypedMemo } from '../../../common/types';
import { warmupMessages as messages } from './warmupMessages';
import { warmupDisplayDay } from './warmupDay';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TOP = Math.max(...OUTREACH_RAMP.map((step) => step.perDay));

/** The index of the ramp step that holds on `day`. */
export function rampStepOf(day: number): number {
  let index = 0;
  OUTREACH_RAMP.forEach((step, i) => {
    if (day >= step.fromDay) {
      index = i;
    }
  });
  return index;
}

/**
 * The 21-day ramp (design t3): one bar for each step of `OUTREACH_RAMP`, today's step in colour.
 * `day` is 0-based, as the API sends it. The label says the day as the chip says it ("Day 8 of 21").
 */
const RampBarsComponent: React.FC<{ day: number; totalDays: number }> = ({ day, totalDays }) => {
  const intl = useIntl();
  const current = rampStepOf(day);
  const shownDay = warmupDisplayDay({ day, totalDays });
  const label = useMemo(
    () =>
      intl.formatMessage(messages.rampAria, {
        steps: intl.formatList(
          OUTREACH_RAMP.map((step) => intl.formatMessage(messages.rampStep, step)),
          { type: 'unit' }
        ),
        day: shownDay,
      }),
    [intl, shownDay]
  );
  const bars = useMemo(
    () =>
      OUTREACH_RAMP.map((step, i) => (
        <span
          key={step.fromDay}
          className={clsx(i < current && styles.rampDone, i === current && styles.rampNow)}
          style={{ height: `${Math.max(6, Math.round((step.perDay / TOP) * 100))}%` }}
        />
      )),
    [current]
  );
  const axis = useMemo(
    () => ({
      caps: OUTREACH_RAMP.map((step) => <span key={step.fromDay}>{step.perDay}</span>),
      days: OUTREACH_RAMP.map((step, i) => (
        <span key={step.fromDay}>
          {i === 0 ? intl.formatMessage(messages.rampDay0, { day: step.fromDay }) : step.fromDay}
        </span>
      )),
    }),
    [intl]
  );
  return (
    <div data-testid="warmup-ramp">
      <div className={styles.ramp} role="img" aria-label={label}>
        {bars}
      </div>
      <div className={styles.rampAxis} aria-hidden>
        {axis.caps}
      </div>
      <div className={styles.rampAxis} aria-hidden>
        {axis.days}
      </div>
    </div>
  );
};

export const RampBars = TypedMemo(RampBarsComponent);
